// Бутон-филтър на справка: „Доставчик ▾" с брояч на избраните, а при клик —
// попъп с отметки (всяка с брой документи), търсене при дълъг списък и
// Изчисти / Готово. Попъпът е antd Popover → Esc го затваря и общият пазач за
// отворен слой го вижда. ↑/↓ местят фокуса по опциите, Space ги отмята.
import { useMemo, useRef, useState } from "react";
import { Popover } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useInventoryShell } from "../shell/InventoryShellContext";
import { LINK_BUTTON_CLASS, PRIMARY_BUTTON_CLASS } from "../list/buttonStyles";
import { CheckBox } from "./CheckBox";

export type FilterOption = {
  value: string;
  label: string;
  count?: number;
};

type Props = {
  label: string;
  title?: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  searchable?: boolean;
};

export const FilterButton = ({
  label,
  title,
  options,
  selected,
  onChange,
  searchable = options.length > 8,
}: Props) => {
  const { t } = useTranslation();
  const { isNarrow } = useInventoryShell();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement | null>(null);

  const visible = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return options;
    return options.filter((option) =>
      words.every((word) => option.label.toLowerCase().includes(word)),
    );
  }, [options, query]);

  const toggle = (value: string) =>
    onChange(
      selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value],
    );

  const onListKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const buttons = Array.from(
      listRef.current?.querySelectorAll<HTMLButtonElement>("button[data-option]") ?? [],
    );
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === "ArrowDown" ? index + 1 : index - 1;
    if (next < 0 || next >= buttons.length) return;
    event.preventDefault();
    buttons[next].focus();
  };

  const count = selected.length;
  const content = (
    <div className="flex max-h-[420px] w-[280px] flex-col overflow-hidden">
      <div className="flex flex-col gap-2 px-3.5 pb-2 pt-3">
        <span className="text-[11px] font-semibold uppercase leading-[normal] tracking-[0.1em] text-muted">
          {title ?? label}
        </span>
        {searchable && (
          <input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("inventoryPage.filterSearch")}
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                listRef.current?.querySelector<HTMLButtonElement>("button[data-option]")?.focus();
              }
            }}
            className={`w-full rounded-lg border border-shade-3 bg-surface px-2.5 py-[7px] text-ink outline-none focus:border-brand ${
              isNarrow ? "text-base" : "text-[13px]"
            }`}
          />
        )}
      </div>
      <div
        ref={listRef}
        onKeyDown={onListKeyDown}
        className="min-h-0 flex-1 overflow-auto px-1.5 pb-1.5"
      >
        {visible.map((option) => {
          const checked = selected.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              data-option
              role="checkbox"
              aria-checked={checked}
              onClick={() => toggle(option.value)}
              className={`flex w-full cursor-pointer items-center gap-2.5 rounded-[7px] border-none px-2 py-2 text-left text-[13.5px] leading-[normal] text-ink outline-none hover:bg-shade-1 focus-visible:shadow-[inset_0_0_0_2px_var(--color-brand)] ${
                checked ? "bg-brand-tint" : "bg-transparent"
              }`}
            >
              <CheckBox checked={checked} />
              <span className="min-w-0 flex-1 truncate">{option.label}</span>
              {option.count != null && (
                <span className="font-sheet text-xs text-muted">{option.count}</span>
              )}
            </button>
          );
        })}
        {visible.length === 0 && (
          <div className="px-2 py-3 text-[13px] text-muted">{t("inventoryPage.filterNoMatch")}</div>
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-shade-2 px-3 py-2.5">
        <button type="button" onClick={() => onChange([])} className={LINK_BUTTON_CLASS}>
          {t("inventoryPage.filterClear")}
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setOpen(false)}
          className={`${PRIMARY_BUTTON_CLASS} !px-3.5 !py-[7px] !text-[13px]`}
        >
          {t("inventoryPage.filterDone")}
        </button>
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
      trigger="click"
      placement="bottomLeft"
      arrow={false}
      content={content}
      styles={{ body: { padding: 0 } }}
      destroyOnHidden
    >
      <button
        type="button"
        className={`flex shrink-0 cursor-pointer items-center gap-[7px] whitespace-nowrap rounded-[9px] border px-3 py-2 text-[13.5px] leading-[normal] text-ink outline-none focus-visible:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
          count ? "border-brand/45 bg-brand-tint" : "border-shade-3 bg-surface hover:border-shade-5"
        }`}
      >
        {label}
        {count > 0 && (
          <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-[9px] bg-brand px-[5px] text-[11px] font-semibold text-white">
            {count}
          </span>
        )}
        <DownOutlined className="text-[10px] text-faint" />
      </button>
    </Popover>
  );
};

// Активните филтри като чипове под лентата: „Доставчик: Метро ×" + „Изчисти всички"
export type ActiveFilterChip = { key: string; label: string; onRemove: () => void };

export const ActiveFilterChips = ({
  chips,
  onClearAll,
}: {
  chips: ActiveFilterChip[];
  onClearAll: () => void;
}) => {
  const { t } = useTranslation();
  if (!chips.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => (
        <button
          key={chip.key}
          type="button"
          onClick={chip.onRemove}
          className="flex cursor-pointer items-center gap-1.5 rounded-full border-none bg-brand-tint px-2.5 py-[5px] text-[12.5px] leading-[normal] text-brand-deep-ink hover:bg-brand-tint2"
        >
          {chip.label}
          <span className="text-sm leading-none">×</span>
        </button>
      ))}
      <button type="button" onClick={onClearAll} className={LINK_BUTTON_CLASS}>
        {t("inventoryPage.filterClearAll")}
      </button>
    </div>
  );
};

export default FilterButton;
